import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type { UseQueryOptions } from 'react-query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type {
  CreateFlexGroup,
  FlexGroup,
  FlexGroupGrid,
  FlexGroupGridItem,
  FlexGroupPagedQuery,
  OrganizationUnitGridItem,
  UpdateFlexGroup,
  UserGridItem,
} from '../dtos';
import type { FlexGroupMembershipGridItem } from '../dtos/FlexGroup/FlexGroupMembershipGridItem';
import type { FlexGroupOrganization } from '../dtos/FlexGroup/FlexGroupOrganization';
import type { FlexGroupUser } from '../dtos/FlexGroup/FlexGroupUser';
import type { FlexGroupType } from '../enums';
import apiClient from '../Utils/apiClient';
import { useCustomToast } from './useCustomToast';
import { longStaleTime } from './useEnums';

const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_FLEX_GROUP_API
const url = `${baseUrl}/${apiUrl}`

export const useGetAll = (search: FlexGroupPagedQuery) => {
  return useQuery<AxiosResponse<FlexGroupGrid>, AxiosError>({
    queryKey: ['flexGroups', search],
    queryFn: () => axios.post<FlexGroupGrid>(`${url}/GetAllFlexGroups`, search)
  })
}

const useGetByUserId = (userId: string, options: UseQueryOptions<FlexGroupGridItem[]>) => {
  return useQuery<FlexGroupGridItem[]>({
    queryKey: ['flexGroupByUserId', userId],
    queryFn: () => axios.get<FlexGroupGridItem[]>(`${url}/GetFlexGroupsByUserId`, { params: { userId } }).then(x => x.data),
    ...options,
  })
}

const useGetOrganizationMembershipsKey = 'flexGroupOrganizationMemberships';
const useGetOrganizationMemberships = (organizationUnitId: number, type: FlexGroupType | undefined, options: UseQueryOptions<FlexGroupMembershipGridItem[]>) => {
  return useQuery<FlexGroupMembershipGridItem[]>({
    queryKey: [useGetOrganizationMembershipsKey, organizationUnitId, type],
    queryFn: () => axios.get<FlexGroupMembershipGridItem[]>(`${url}/GetOrganizationMemberships`, { params: { organizationUnitId, type } }).then(x => x.data),
    ...options,
  })
}

const flexGroupKey = (flexGroupId?: number) => ['flexGroup', flexGroupId]

const useGetById = (flexGroupId?: number) => {
  return useQuery({
    queryKey: flexGroupKey(flexGroupId),
    queryFn: () => axios.get<FlexGroup>(`${url}/GetById`, { params: { flexGroupId } }).then(x => x.data),
    enabled: !!flexGroupId,
  })
}

export const useCreate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (flexGroup: CreateFlexGroup) => axios.post<FlexGroup>(`${url}/Create`, flexGroup).then(x => x.data),
    onSuccess: (data) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(data.id), (_) => data)
    }
  })
}

export const useUpdate = (flexGroupId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (flexGroup: UpdateFlexGroup) => axios.post(`${url}/Update`, flexGroup),
    onSuccess: (_, update) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        ...update
      }) : undefined)
    }
  })
}

interface MemberOrganizationProps {
  flexGroupId: number
  organizationUnitId: number
  includeSubordinate: boolean
}
const useAddMemberOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation<FlexGroupOrganization, AxiosError, MemberOrganizationProps>({
    mutationFn: (payload: MemberOrganizationProps) =>
      axios.post<FlexGroupOrganization>(`${url}/AddMemberOrganization`, payload).then(x => x.data),
    onSuccess: (newMemberOrganization, { flexGroupId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        memberOrganizations: [...flexGroup.memberOrganizations, newMemberOrganization]
      }) : undefined)
      queryClient.invalidateQueries({
        queryKey: [useGetOrganizationMembershipsKey, newMemberOrganization.organizationUnitId]
      })
    }
  }
  )
}

const useUpdateMemberOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: MemberOrganizationProps) =>
      axios.post(`${url}/UpdateMemberOrganization`, payload),
    onSuccess: (_, { flexGroupId, organizationUnitId, includeSubordinate }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        memberOrganizations: flexGroup.memberOrganizations.map(x => x.organizationUnitId === organizationUnitId ? { ...x, includeSubordinate } : x)
      }) : undefined)
      queryClient.invalidateQueries({
        queryKey: [useGetOrganizationMembershipsKey, organizationUnitId]
      })
    }
  })
}

const useRemoveMemberOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { flexGroupId: number, organizationUnitId: number }) =>
      axios.post(`${url}/RemoveMemberOrganization`, payload),
    onSuccess: (_, { flexGroupId, organizationUnitId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        memberOrganizations: flexGroup.memberOrganizations.filter(x => x.organizationUnitId !== organizationUnitId)
      }) : undefined)
      queryClient.invalidateQueries({
        queryKey: [useGetOrganizationMembershipsKey, organizationUnitId]
      })
    }
  })
}

interface MemberUserProps {
  flexGroupId: number
  userId: string
}

const useAddMemberUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: MemberUserProps) => axios.post<FlexGroupUser>(`${url}/AddMemberUser`, payload).then(x => x.data),
    onSuccess: (newMemberUser, { flexGroupId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        memberUsers: [...flexGroup.memberUsers, newMemberUser]
      }) : undefined)
    }
  })
}

const useRemoveMemberUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: MemberUserProps) => axios.post(`${url}/RemoveMemberUser`, payload),
    onSuccess: (_, { flexGroupId, userId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => {
        return flexGroup ? ({
          ...flexGroup,
          memberUsers: flexGroup.memberUsers.filter(x => x.userId !== userId)
        }) : undefined
      })
    }
  })
}

interface SupervisorOrganizationProps {
  flexGroupId: number
  organizationUnitId: number
}

const useAddSupervisorOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SupervisorOrganizationProps) => axios.post<FlexGroupOrganization>(`${url}/AddSupervisorOrganization`, payload).then(x => x.data),
    onSuccess: (newSupervisorOrganization, { flexGroupId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        supervisorOrganizations: [...flexGroup.supervisorOrganizations, newSupervisorOrganization]
      }) : undefined)
    }
  })
}

const useRemoveSupervisorOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SupervisorOrganizationProps) => axios.post(`${url}/RemoveSupervisorOrganization`, payload),
    onSuccess: (_, { flexGroupId, organizationUnitId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        supervisorOrganizations: flexGroup.supervisorOrganizations.filter(x => x.organizationUnitId !== organizationUnitId)
      }) : undefined)
    }
  })
}


interface SupervisorUserProps extends Omit<FlexGroupUser, 'user'> { }

const useAddSupervisorUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SupervisorUserProps) => axios.post<FlexGroupUser>(`${url}/AddSupervisorUser`, payload).then(x => x.data),
    onSuccess: (newSupervisorUser, { flexGroupId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        supervisorUsers: [...flexGroup.supervisorUsers, newSupervisorUser]
      }) : undefined)
    }
  })
}

const useRemoveSupervisorUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SupervisorUserProps) => axios.post(`${url}/RemoveSupervisorUser`, payload),
    onSuccess: (_, { flexGroupId, userId }) => {
      queryClient.setQueryData<FlexGroup | undefined>(flexGroupKey(flexGroupId), (flexGroup) => flexGroup ? ({
        ...flexGroup,
        supervisorUsers: flexGroup.supervisorUsers.filter(x => x.userId !== userId)
      }) : undefined)
    }
  })
}

export const useGetFlexGroupTypes = () => {
  return useQuery({
    queryKey: ['flexGroupTypes'],
    queryFn: async () => {
      const result = await apiClient.get<{
        type: FlexGroupType,
        name: string,
        description: string,
      }[]>(`${url}/GetFlexGroupTypes`)

      return result.data
    },
    staleTime: longStaleTime
  })
}

export const useGetFlexGroupType = (type?: FlexGroupType) => {
  const { data: types } = useGetFlexGroupTypes()

  return types?.find(t => t.type === type)
}

export const useFlexGroup = (flexGroupId: number) => {
  const { data: flexGroup, isError, isLoading, refetch } = useGetById(flexGroupId)
  const { successToast, errorToast } = useCustomToast()

  const { mutateAsync: updateMutateAsync } = useUpdate(flexGroupId)
  const updateAsync = (flexGroup: UpdateFlexGroup) => updateMutateAsync(flexGroup)
    .then(successToast('Flexibilní skupina byla úspěšně upravena'))
    .catch(errorToast('Chyba při úpravě flexibilní skupiny')) as Promise<void>

  const { mutateAsync: addMemberOrganizationMutateAsync } = useAddMemberOrganization()
  const addMemberOrganizationAsync = (organizationUnitId: number, includeSubordinate: boolean) => addMemberOrganizationMutateAsync({ flexGroupId, organizationUnitId, includeSubordinate })
    .then(successToast('Organizace byla úspěšně přidána do flexibilní skupiny'))
    .catch(errorToast('Chyba při přidávání organizace do flexibilní skupiny')) as Promise<OrganizationUnitGridItem>

  const { mutateAsync: updateMemberOrganizationMutateAsync } = useUpdateMemberOrganization()
  const updateMemberOrganizationAsync = (organizationUnitId: number, includeSubordinate: boolean) => updateMemberOrganizationMutateAsync({ flexGroupId, organizationUnitId, includeSubordinate })
    .then(successToast(includeSubordinate ? 'Zahrnutí podřízených organizací bylo zapnuto' : 'Zahrnutí podřízených organizací bylo vypnuto'))
    .catch(errorToast('Chyba při změně zahrnutí podřízených organizací.')) as Promise<void>

  const { mutateAsync: removeMemberOrganizationMutateAsync } = useRemoveMemberOrganization()
  const removeMemberOrganizationAsync = (organizationUnitId: number) => removeMemberOrganizationMutateAsync({ flexGroupId, organizationUnitId })
    .then(successToast('Organizace byla úspěšně odebrána z flexibilní skupiny'))
    .catch(errorToast('Chyba při odebírání organizace z flexibilní skupiny')) as Promise<void>

  const { mutateAsync: addMemberUserMutateAsync } = useAddMemberUser()
  const addMemberUserAsync = (userId: string) => addMemberUserMutateAsync({ flexGroupId, userId })
    .then(successToast('Uživatel byl úspěšně přidán do flexibilní skupiny'))
    .catch(errorToast('Chyba při přidávání uživatele do flexibilní skupiny')) as Promise<UserGridItem>

  const { mutateAsync: removeMemberUserMutateAsync } = useRemoveMemberUser()
  const removeMemberUserAsync = (userId: string) => removeMemberUserMutateAsync({ flexGroupId, userId })
    .then(successToast('Uživatel byl úspěšně odebrán z flexibilní skupiny'))
    .catch(errorToast('Chyba při odebírání uživatele z flexibilní skupiny')) as Promise<void>

  const { mutateAsync: addSupervisorOrganizationMutateAsync } = useAddSupervisorOrganization()
  const addSupervisorOrganizationAsync = (organizationUnitId: number) => addSupervisorOrganizationMutateAsync({ flexGroupId, organizationUnitId })
    .then(successToast('Organizaci byl přidělen přístup k flexibilní skupině'))
    .catch(errorToast('Chyba při přidělování přístupu k flexibilní skupině')) as Promise<OrganizationUnitGridItem>

  const { mutateAsync: removeSupervisorOrganizationMutateAsync } = useRemoveSupervisorOrganization()
  const removeSupervisorOrganizationAsync = (organizationUnitId: number) => removeSupervisorOrganizationMutateAsync({ flexGroupId, organizationUnitId })
    .then(successToast('Organizaci byl odebrán přístup k flexibilní skupině'))
    .catch(errorToast('Chyba při odebírání přístupu k flexibilní skupině')) as Promise<void>

  const { mutateAsync: addSupervisorUserMutateAsync } = useAddSupervisorUser()
  const addSupervisorUserAsync = (userId: string) => addSupervisorUserMutateAsync({ flexGroupId, userId })
    .then(successToast('Uživatel byl přidělen přístup k flexibilní skupině'))
    .catch(errorToast('Chyba při přidělování přístupu k flexibilní skupině')) as Promise<UserGridItem>

  const { mutateAsync: removeSupervisorUserMutateAsync } = useRemoveSupervisorUser()
  const removeSupervisorUserAsync = (userId: string) => removeSupervisorUserMutateAsync({ flexGroupId, userId })
    .then(successToast('Uživatel byl odebrán přístup k flexibilní skupině'))
    .catch(errorToast('Chyba při odebírání přístupu k flexibilní skupině')) as Promise<void>

  return {
    flexGroup,
    isError,
    isLoading,
    refetch,
    updateAsync,
    addMemberOrganizationAsync,
    updateMemberOrganizationAsync,
    removeMemberOrganizationAsync,
    addMemberUserAsync,
    removeMemberUserAsync,
    addSupervisorOrganizationAsync,
    removeSupervisorOrganizationAsync,
    addSupervisorUserAsync,
    removeSupervisorUserAsync,
  }
}

export const useUserFlexGroups = (userId: string, enabled?: boolean) => {
  const { data: flexGroups, isError, isLoading, refetch } = useGetByUserId(userId, { enabled })

  return {
    flexGroups,
    isError,
    isLoading,
    refetch,
  }
}

export const useOrganizationFlexGroups = (organizationUnitId: number, type: FlexGroupType | undefined, enabled?: boolean) => {
  const { data: flexGroups, isError, isLoading, refetch } = useGetOrganizationMemberships(organizationUnitId, type, { enabled })

  return {
    flexGroups,
    isError,
    isLoading,
    refetch,
  }
}